// ---------------------------------------------------------------------------
// Shared AudioContext
// ---------------------------------------------------------------------------
// Mobile browsers (notably iOS Safari) require AudioContext creation/resume to
// happen INSIDE a user gesture. The timer fires its alert tens of seconds
// later — long after any gesture — so we lazily build a single context the
// first time the user checks a set and re-use it from then on. By the time the
// alert plays, the context has long been unlocked.
//
// A context is not always "running" or "suspended": iOS leaves it
// "interrupted" after the phone was locked (or a call came in). Anything but
// "running" needs a resume(), otherwise the chime is scheduled on a stopped
// context and never heard.

/** How long a resume() may take for the chime to still count as "on time". */
const RESUME_GRACE_MS = 1500;

let sharedAudioCtx: AudioContext | null = null;

const resolveAudioCtxClass = (): typeof AudioContext | null => {
  if (typeof window === "undefined") return null;
  return (
    window.AudioContext ||
    (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext ||
    null
  );
};

const getOrCreateAudioContext = (): AudioContext | null => {
  if (sharedAudioCtx && sharedAudioCtx.state !== "closed") return sharedAudioCtx;
  const Ctor = resolveAudioCtxClass();
  if (!Ctor) return null;
  try {
    sharedAudioCtx = new Ctor();
    return sharedAudioCtx;
  } catch {
    return null;
  }
};

const isRunning = (ctx: AudioContext): boolean =>
  // "interrupted" is WebKit-only, so it is not in the AudioContextState type.
  (ctx.state as string) === "running";

/**
 * Called from a user gesture (set-check) to unlock the AudioContext on iOS so
 * the alert tone can play later, when no gesture is active. Safe to call
 * repeatedly; a running context is left alone.
 */
export const primeAudio = () => {
  const ctx = getOrCreateAudioContext();
  if (!ctx || isRunning(ctx)) return;
  void ctx.resume().catch(() => undefined);
};

/**
 * Called when the app is back on screen: brings back a context iOS stopped
 * while the phone was locked, so a rest that ends later is heard. Never
 * creates a context (that needs a gesture).
 */
export const resumeAudio = () => {
  const ctx = sharedAudioCtx;
  if (!ctx || ctx.state === "closed" || isRunning(ctx)) return;
  void ctx.resume().catch(() => undefined);
};

/**
 * A soft, friendly chime via the Web Audio API (synthesized, no asset
 * download). Clients found the old square-wave alarm scary (Trello #1111), so
 * this one is a bell-like rising major arpeggio: sine + a little triangle, a
 * gentle attack and a natural fade instead of beeps held at full volume.
 *
 * Volume goes through a master gain and a DynamicsCompressor so the overlapping
 * note tails never clip.
 */
const playChime = (ctx: AudioContext) => {
  try {
    const start = ctx.currentTime;

    // Master path: gain → soft-knee compressor → destination.
    const master = ctx.createGain();
    master.gain.value = 0.8;
    const compressor = ctx.createDynamicsCompressor();
    compressor.threshold.value = -14;
    compressor.knee.value = 18;
    compressor.ratio.value = 3;
    compressor.attack.value = 0.01;
    compressor.release.value = 0.25;
    master.connect(compressor).connect(ctx.destination);

    // A single "note": a sine with a quiet triangle on top for a little warmth,
    // under one envelope that rises softly and then fades out like a bell.
    const note = (
      offset: number,
      duration: number,
      frequency: number,
      peakGain: number,
    ) => {
      const env = ctx.createGain();
      env.gain.setValueAtTime(0, start + offset);
      env.gain.linearRampToValueAtTime(peakGain, start + offset + 0.02);
      env.gain.exponentialRampToValueAtTime(0.0001, start + offset + duration);
      env.connect(master);

      const layers: { type: OscillatorType; freq: number; gain: number }[] = [
        { type: "sine", freq: frequency, gain: 1 },
        { type: "triangle", freq: frequency, gain: 0.3 },
      ];
      for (const layer of layers) {
        const osc = ctx.createOscillator();
        osc.type = layer.type;
        osc.frequency.setValueAtTime(layer.freq, start + offset);
        const oscGain = ctx.createGain();
        oscGain.gain.value = layer.gain;
        osc.connect(oscGain).connect(env);
        osc.start(start + offset);
        osc.stop(start + offset + duration + 0.02);
      }
    };

    // "Ding-ding-ding": a rising C-major arpeggio that reads as "done, ready",
    // with the last note left to ring out.
    note(0.00, 0.9, 659.25, 0.5);   // E5
    note(0.18, 0.9, 783.99, 0.5);   // G5
    note(0.36, 1.4, 1046.5, 0.55);  // C6

    // We deliberately do NOT close the shared context — keeping it warm means
    // the next set-check doesn't have to re-unlock audio on mobile.
  } catch {
    // Audio is best-effort; if it fails we silently fall through to vibration only.
  }
};

/** Plays the end-of-rest chime, then vibrates if the device supports it. */
export const playEndAlert = () => {
  const ctx = getOrCreateAudioContext();
  if (ctx) {
    if (isRunning(ctx)) {
      playChime(ctx);
    } else {
      // Notes scheduled on a stopped context would sound whenever it next
      // resumes — a beep out of nowhere minutes later — so only play once it is
      // running again, and only if that happened right away. On iOS a resume
      // outside a gesture can be refused; then only the vibration is left.
      const requestedAt = Date.now();
      ctx
        .resume()
        .then(() => {
          if (isRunning(ctx) && Date.now() - requestedAt <= RESUME_GRACE_MS) {
            playChime(ctx);
          }
        })
        .catch(() => undefined);
    }
  }

  try {
    if (typeof navigator !== "undefined" && typeof navigator.vibrate === "function") {
      // iOS Safari ignores `vibrate` entirely; Android Chrome/Firefox/Edge
      // respect it.
      navigator.vibrate([220, 100, 220, 100, 220, 100, 520]);
    }
  } catch {
    // Vibration is best-effort.
  }
};
